/**
 * 화면에 쓰는 **UI 그림 목록** — 단일 정의(웹 렌더·관리자 목록 공용).
 *
 * ## 왜 필요한가
 * UI 그림 경로가 컴포넌트마다 문자열로 박혀 있었다(`/art/ui/nav/myroom.webp` 등).
 * 그래서 운영자가 버튼 그림 하나를 못 바꿨다 — 개발자가 파일을 커밋해야 했다.
 * 여기 등록해 두면 관리자 [그림]에서 갈아끼울 수 있다(콘텐츠 주도).
 *
 * ## 세 겹 폴백
 * 1. 게시된 그림(`ui.<id>`) — 운영자가 올린 것
 * 2. 번들 기본 그림(`bundled`) — 저장소에 커밋된 것
 * 3. 이모지(`emoji`) — 둘 다 없거나 로드 실패
 *
 * 🧩 **2026-09-27 — `act` 26종에 그림이 들어왔다.** 예전엔 12종 모두 파일이 없어 이모지로 나갔다.
 * 격자 두 장(gpt_image_2 · `assets-src/ui/act/_sheets`)을 `scripts/slice-icon-sheet.py` 로 잘랐다.
 * 화면마다 한 장으로 묶어 받는 것은 아래 `UI_ATLAS`(조치계획서 ■2 요청 수).
 */

export type UiArtGroup = "act" | "nav" | "top" | "stick";

export interface UiArtSlot {
  /** 그룹 안에서의 이름. 매니페스트 키는 `ui.<group>.<name>`. */
  name: string;
  group: UiArtGroup;
  /** 운영자에게 보여줄 이름. */
  label: string;
  /** 저장소에 커밋된 기본 그림 경로(없으면 이모지로 간다). */
  bundled?: string;
  /** 최후 폴백 — 그림이 없어도 뜻은 전달된다. */
  emoji: string;
}

export const UI_ART_GROUP_LABEL: Record<UiArtGroup, string> = {
  act: "동작 아이콘",
  nav: "아래 메뉴",
  top: "위쪽 버튼",
  stick: "조이스틱",
};

/** `act` 는 아직 그림이 없다 — 이모지로 나오고 있다(운영자가 채우면 바로 바뀐다). */
const ACT: readonly [string, string, string][] = [
  ["mission", "미션", "📜"],
  ["camera", "사진", "📸"],
  ["sound_on", "소리 켜짐", "🔊"],
  ["sound_off", "소리 꺼짐", "🔇"],
  ["map", "지도", "🗺️"],
  ["pin", "위치 핀", "📍"],
  ["sparkle", "반짝임", "✨"],
  ["megaphone", "알림", "📢"],
  ["prev", "이전", "‹"],
  ["next", "다음", "›"],
  ["pause", "멈춤", "⏸️"],
  ["subtitle", "자막", "💬"],
  // ── 2026-09-27 아이 화면 단추(조치계획서 ■1 — 4~7세는 글자·이모지 대신 그림으로 고른다)
  ["close", "닫기", "✕"],
  ["back", "돌아가기", "↩️"],
  ["play", "영상 보기", "▶️"],
  ["door", "들어가기", "🚪"],
  ["maker", "친구 만들기", "🖍️"],
  ["star", "별·미션", "⭐"],
  ["mybook", "내 그림책", "📖"],
  ["gallery", "우리 반 전시장", "🖼️"],
  ["key", "내 코드", "🔑"],
  ["print", "종이로 뽑기", "🖨️"],
  ["home", "처음으로", "🏠"],
  ["cube", "3D로 보기", "🧊"],
  ["village", "마을", "🏡"],
  ["gyeongju", "경주", "🏯"],
  // ── 2026-09-27 어른이 쓰는 단추(보호자 화면·설정·꾸미기·안내) — 어른도 글보다 그림을 먼저 본다
  ["stamp", "스탬프 랠리", "🏃"],
  ["guide", "함께 보는 법", "📖"],
  ["teddy", "아이 화면", "🧸"],
  ["grid", "모든 메뉴", "🏠"],
  ["leaf", "움직임 줄이기", "🍃"],
  ["reset", "처음부터 다시", "🧹"],
  ["privacy", "개인정보", "🔒"],
  ["tools", "운영자 화면", "🔧"],
  ["palette", "직접 꾸미기", "🎨"],
  ["dice", "랜덤 조합", "🎲"],
  ["check", "저장·완료", "✅"],
  ["passport", "스탬프 여권", "📘"],
  ["document", "고지·문서", "📜"],
  ["load", "불러오기", "📥"],
  ["skip", "건너뛰기", "⏭️"],
  ["globe", "언어", "🌐"],
];

const NAV: readonly [string, string, string][] = [
  ["myroom", "마이룸", "🧒"],
  ["library", "도서관", "📚"],
  ["verse", "체험존", "⭐"],
  ["event", "이벤트", "🎁"],
  ["store", "굿즈", "🛍️"],
  ["community", "마을 소식", "📢"],
  ["settings", "설정", "⚙️"],
  /**
   * ✨ **마법(카메라 AR)** — 영유아 모드의 세 단추 중 하나(조치계획서 ■1).
   *
   * 평소 하단 메뉴에는 없다(주소로만 들어간다). 영유아 모드에서는 「전자책·마법·체험공간」
   * 셋만 보이므로 여기에 자리가 필요하다. 2026-09-25 에 나머지 일곱과 같은 수채 화풍으로
   * 그렸다(카메라 + 반짝임 — 「비추면 살아난다」를 글자 없이 말한다).
   */
  ["magic", "마법", "✨"],
  /**
   * 🔒 **보호자** — 영유아 모드에서 굿즈·행사·소식으로 가는 문(어른 확인 뒤).
   * 아이 화면 네 칸 중 이 칸만 이모지라 튀었다(2026-09-26). 자물쇠에 하트 — 「잠겨 있지만 무서운 곳은 아니다」.
   */
  ["family", "보호자", "🔒"],
];

const TOP: readonly [string, string, string][] = [
  ["music", "음악", "🎵"],
  ["bell", "알림", "🔔"],
  ["menu", "메뉴", "☰"],
];

const STICK: readonly [string, string, string][] = [
  ["base", "조이스틱 바닥", "⭕"],
  ["thumb", "조이스틱 손잡이", "⚫"],
];

/**
 * 🔴 **번들 그림이 아직 없는 자리** — 여기 든 것은 `bundled` 를 안 준다.
 *
 * 규칙이 그룹 단위(`/art/ui/nav/<이름>.webp`)라 **파일이 없어도 주소가 나온다.** 그러면
 * 화면에 **깨진 그림 아이콘**이 뜬다 — 이모지 폴백이 있는데도 안 쓰인다(2026-09-22에 실제로
 * 마법 단추가 그랬다). 파일을 넣으면 이 줄을 지운다. 검사가 둘을 맞춰 준다(`uiArtBundled.test.ts`).
 *
 * 지금은 비어 있다 — `nav:magic` 은 2026-09-25 에 그림이 들어와 빠졌다.
 */
const NO_FILE_YET = new Set<string>([]);

/** 번들 기본 경로 — 그룹마다 폴더가 달라서 표로 둔다(실제 파일 위치와 맞춰야 한다). */
const BUNDLED: Partial<Record<UiArtGroup, (name: string) => string>> = {
  nav: (n) => `/art/ui/nav/${n}.webp`,
  top: (n) => `/art/ui/top/${n}.webp`,
  stick: (n) => `/art/ui/joystick-${n}.webp`,
  act: (n) => `/art/ui/act/${n}.webp`,
};

function build(group: UiArtGroup, rows: readonly [string, string, string][]): UiArtSlot[] {
  return rows.map(([name, label, emoji]) => ({
    name,
    group,
    label,
    emoji,
    bundled: NO_FILE_YET.has(`${group}:${name}`) ? undefined : BUNDLED[group]?.(name),
  }));
}

export const UI_ART: readonly UiArtSlot[] = [
  ...build("act", ACT),
  ...build("nav", NAV),
  ...build("top", TOP),
  ...build("stick", STICK),
];

/** 매니페스트 키. */
export function uiArtKey(group: UiArtGroup, name: string): string {
  return `ui.${group}.${name}`;
}

/** 등록된 UI 그림 키 전체 — 관리자 목록·검증에서 쓴다. */
export function uiArtKeys(): string[] {
  return UI_ART.map((s) => uiArtKey(s.group, s.name));
}

export function uiArtSlot(group: UiArtGroup, name: string): UiArtSlot | undefined {
  return UI_ART.find((s) => s.group === group && s.name === name);
}

/**
 * 🧩 **화면마다 아이콘을 한 장으로 묶어 받는다** (2026-09-27 · 조치계획서 ■2).
 *
 * 아이콘 21개를 단추마다 따로 받으면 요청이 그만큼 는다 — 체험공간은 39개(목표 40)라 8개만 더해도
 * 약속을 넘는다. 그래서 **한 화면에서 쓰는 것을 가로 한 줄로 이어 붙인 한 장**을 받고, 칸을 잘라 보여 준다.
 *
 * · 칸 순서가 곧 위치다(가로 `UI_ATLAS_CELL` px 씩) — 좌표 표를 따로 두지 않는다(둘이 갈리면 엉뚱한 그림이 뜬다)
 * · 묶음은 `pnpm ui:atlas` 가 낱장 파일(`bundled`)로 굽는다. 낱장을 바꾸고 안 구우면 검사가 잡는다(`uiAtlas.test.ts`)
 * · 🔴 **운영자가 관리자에서 올린 그림이 이긴다** — 그 하나만 낱장으로 받는다(묶음은 저장소 기본값이다)
 * · 같은 아이콘이 여러 묶음에 들어가도 된다(화면마다 한 장이면 된다 — 픽셀 몇 KB 보다 요청 하나가 비싸다)
 * · 첫 화면 위·아래 메뉴는 아직 낱장이다 — 영유아 모드 게시 뒤 요청 25(목표 27)라 급하지 않다. 옮기면 −6
 */
export type UiAtlasBundle = "home" | "shelf" | "book" | "verse" | "world" | "ar" | "settings" | "hero" | "family" | "info";
export const UI_ATLAS_CELL = 128;
export const UI_ATLAS: Readonly<Record<UiAtlasBundle, readonly string[]>> = {
  home: ["act:door", "act:maker"],
  // 책장(/book) — 닫기 하나. 책 묶음(52KB)을 책장에서 받으면 전자책 용량이 목표(0.40MB)에 닿는다. 책을 펴면 book
  shelf: ["act:close"],
  book: ["act:prev", "act:next", "act:close", "act:back", "act:play", "act:sound_on", "act:pause", "act:subtitle", "nav:magic", "act:globe", "act:sparkle"],
  verse: ["act:door", "act:maker", "act:mybook", "act:gallery", "act:key", "act:print", "act:home", "act:load"],
  world: ["act:back", "act:star", "act:camera", "top:menu", "act:village", "nav:library", "act:gyeongju", "nav:event", "nav:store", "act:next", "act:skip"],
  ar: ["act:close", "nav:magic", "act:cube", "act:play", "act:camera", "act:home", "act:back"],
  // 🔴 창 두 개(설정·꾸미기)는 첫 화면 **위에** 열린다 — 첫 화면 묶음에 넣으면 아이 첫 화면이 무거워진다(■2 0.68MB).
  //    그래서 열릴 때만 받는 제 묶음을 창 안에서 감싼다(UiAtlas 는 겹쳐 쓸 수 있다 — 안쪽이 이긴다)
  settings: ["act:close", "act:sound_on", "act:sound_off", "act:leaf", "act:guide", "act:teddy", "nav:family", "act:reset", "act:privacy", "act:tools"],
  hero: ["act:close", "act:sparkle", "act:palette", "act:dice", "act:check"],
  family: ["nav:store", "nav:event", "nav:community", "act:stamp", "act:guide", "act:teddy", "act:grid", "act:back"],
  // 어른이 읽는 안내 화면들(함께 보는 법·개인정보·고지·마을 소식·부모 그림책·코드 복원·랠리·굿즈)
  info: ["act:home", "act:megaphone", "act:privacy", "act:document", "act:print", "act:guide", "act:passport", "act:door", "act:close", "act:maker", "act:load", "nav:store"],
};

/** 묶음 그림 주소(저장소 기본). */
export function uiAtlasUrl(bundle: UiAtlasBundle): string {
  return `/art/ui/atlas/${bundle}.webp`;
}

/** 그 묶음에서 몇 번째 칸인가 — 없으면 -1(낱장·이모지로 간다). */
export function uiAtlasIndex(bundle: UiAtlasBundle | undefined, group: UiArtGroup, name: string): number {
  if (!bundle) return -1;
  return UI_ATLAS[bundle].indexOf(`${group}:${name}`);
}

/**
 * 그림을 옆에 둘 때 **문구 앞뒤의 이모지를 뗀다** — 운영자 문구(SiteText)에 「체험 공간 입장 ▶️」처럼
 * 이모지가 박혀 있어, 그림과 같이 두면 뜻이 두 번 나온다. 문구 자체는 안 고친다(운영자가 쓴 그대로 둔다).
 */
export function withoutEdgeEmoji(text: string): string {
  const EDGE = /^[\p{Extended_Pictographic}\u{FE0F}\u{200D}\s]+|[\p{Extended_Pictographic}\u{FE0F}\u{200D}\s]+$/gu;
  const out = text.replace(EDGE, "").trim();
  return out || text; // 이모지뿐인 문구는 그대로(빈 단추를 만들지 않는다)
}

/**
 * 매니페스트에 없지만 **웹이 번들 파일로 보여주는** 그림.
 *
 * 랜딩 캐릭터는 게시본이 없어도 저장소에 커밋된 그림으로 나온다. 이 표가 웹(landingAssets)과
 * 관리자(미리보기) 두 곳에 따로 있으면, 한쪽만 고쳤을 때 **관리자만 "비어 있음"이라고
 * 거짓말한다.** 그래서 여기 하나로 둔다.
 */
export const BUNDLED_ART: Readonly<Record<string, string>> = {
  /**
   * 메인 화면 왼쪽 = **우리나비 출판사의 회사 캐릭터(검은 고양이)**. (2026-08-19 확정)
   *
   * 슬롯 이름의 `greg` 는 옛 이름이라 그림과 다르다 — 관리자 표시는 이미 위치 기준
   * (「메인 왼쪽 캐릭터」)이다. 키를 바꾸면 게시본·매니페스트가 전부 어긋나므로 두었다.
   *
   * 🔴 **주인공 그레그(`greg-white`)와 다른 캐릭터다.** 회사 캐릭터는 첫 화면 간판이고,
   * 그레그는 책 속 주인공이라 전자책·AR 포토존에 계속 쓰인다. 같이 바꾸지 말 것.
   *
   * **자세는 화면마다 다르게 쓴다**(같은 캐릭터, 다른 포즈):
   *  - 첫 화면  `read` — 책 읽는 자세. 그림책방 간판이라 이게 맞다(애니 `read-strip`)
   *  - 체험공간 `front`/`down`/`side`/`up` — 서서 걷는 자세. 마을을 돌아다녀야 한다
   * 한쪽 자세로 통일하려다 되돌렸다(2026-08-20) — 첫 화면에서 걷는 그림은 어색하다.
   */
  "landing.char.greg": "/art/chars/greg-cat/read.webp",
  /**
   * 메인 화면 병아리 — **월드 NPC 와 같은 캐릭터**, 파일만 다르다.
   * 첫 화면은 크게 나와서 큰 그림(484~478×640)이 필요하고, 월드는 128px 스프라이트다.
   * 그래서 같은 그림을 두 크기로 둔다(`landing.webp` · `front.webp`).
   * 2026-08-19 이전에는 **서로 다른 그림**이었다(또렷한 쪽 ↔ 수채 톤) — 화면마다 달라 보였다.
   */
  "landing.char.chick": "/art/chars/chick/landing.webp",
};

/** 매니페스트에 없을 때 웹이 쓰는 번들 경로(상대). 없으면 undefined(= 절차적이거나 비어 있음). */
export function bundledArtPath(slotKey: string): string | undefined {
  const direct = BUNDLED_ART[slotKey];
  if (direct) return direct;
  const ui = UI_ART.find((s) => uiArtKey(s.group, s.name) === slotKey);
  return ui?.bundled;
}

/**
 * 그림이 없을 때 화면에 **실제로 나오는 이모지**. 관리자 미리보기가 이걸 그려야
 * "지금 화면이 이렇다"가 된다 — 회색 자리표시자를 그리면 아무 정보도 주지 못한다.
 */
export function bundledArtEmoji(slotKey: string): string | undefined {
  const ui = UI_ART.find((s) => uiArtKey(s.group, s.name) === slotKey);
  return ui?.bundled ? undefined : ui?.emoji;
}
